<template>
    <v-container>
        <v-row>
            <v-col>
                <v-breadcrumbs :items="breadcrumbs"></v-breadcrumbs>
            </v-col>
        </v-row>
        <v-row v-if="loading">
            <v-col>
                <v-skeleton-loader class="field-types-loader" type="heading, list-item, list-item, list-item"> </v-skeleton-loader>
            </v-col>
        </v-row>
        <v-row v-else-if="loadError">
            <v-col>
                <v-alert type="error" variant="tonal">{{ loadError }}</v-alert>
            </v-col>
        </v-row>
        <v-row v-else>
            <v-col v-for="assignment in store.types" :key="assignment.type" cols="12" lg="6">
                <v-card :loading="saving[assignment.type]">
                    <v-card-item>
                        <v-card-title>{{ typeLabel(assignment.type) }}</v-card-title>
                        <v-card-subtitle>{{ assignment.type }}</v-card-subtitle>
                    </v-card-item>
                    <v-card-text>
                        <v-autocomplete
                            v-model="drafts[assignment.type]"
                            :disabled="!store.canEditFieldTypes || busy"
                            :items="itemsByType[assignment.type]"
                            chips
                            closable-chips
                            item-title="title"
                            item-value="value"
                            label="Skjemafelt"
                            multiple
                            no-data-text="Ingen felt funnet"
                        ></v-autocomplete>
                        <v-alert v-for="move in movesFor(assignment.type)" :key="move.source" density="compact" type="info" variant="tonal">
                            Flyttes fra {{ typeLabel(move.source) }}: {{ move.fields.join(', ') }}
                        </v-alert>
                    </v-card-text>
                    <v-card-actions v-if="store.canEditFieldTypes">
                        <v-spacer></v-spacer>
                        <v-btn :disabled="!isDirty(assignment.type) || busy" color="primary" variant="flat" @click="save(assignment.type)">
                            Lagre
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script lang="ts" setup>
import { VSkeletonLoader } from 'vuetify/components';
import { computed, onMounted, reactive, ref } from 'vue';
import { AlertTypes, Events, useEmitter } from '@/features/shared/useEmitter';
import { getErrors } from '@/features/shared/ErrorHelper';
import { useIssueFormMetadataStore } from './issueFormMetadataStore';
import { IssueFormFieldSummary } from './issueFormMetadataModels';

interface FieldItem {
    value: string;
    title: string;
    props?: { subtitle: string };
}

const typeLabels: Record<string, string> = {
    Line: 'Linje',
    Departure: 'Avgang',
    Stop: 'Holdeplass',
    DateOnly: 'Dato',
    TimeOnly: 'Tid',
    DateTime: 'Dato og tid',
    ContactEmail: 'Kontakt-e-post',
    ContactPhone: 'Kontakttelefon'
};

const eventBus = useEmitter();
const store = useIssueFormMetadataStore();

const breadcrumbs = [{ title: 'Skjemafelttyper' }];
const loading = ref(true);
const drafts = reactive<Record<string, string[]>>({});
const saving = reactive<Record<string, boolean>>({});
const loadError = ref('');
const busy = computed(() => Object.values(saving).some(Boolean));

const itemsByType = computed(() => {
    const result: Record<string, FieldItem[]> = {};
    for (const { type } of store.types) {
        result[type] = store.fields.map(field => {
            const source = sourceTypeOf(field.id, type);
            const item: FieldItem = { value: field.id, title: fieldText(field) };
            if (source) item.props = { subtitle: `Flyttes fra ${typeLabel(source)}` };
            return item;
        });
    }
    return result;
});

onMounted(async () => {
    try {
        await store.loadFieldTypes();
        resetDrafts();
    } catch (err: any) {
        loadError.value = errorText(err) ?? 'Kunne ikke laste skjemafelttyper';
    } finally {
        loading.value = false;
    }
});

function errorText(err: any): string | null {
    const status = err?.response?.status;
    if (status === 400 || status === 503) return getErrors(err.response.data)?.text ?? null;
    return null;
}

function typeLabel(type: string) {
    return typeLabels[type] ?? type;
}

function fieldText(field: IssueFormFieldSummary) {
    return `${field.title} (${field.type}) · ${field.id}`;
}

function loadedIds(type: string): string[] {
    return store.types.find(x => x.type === type)?.fieldIds ?? [];
}

function sourceTypeOf(fieldId: string, targetType: string) {
    return store.types.find(x => x.type !== targetType && x.fieldIds.includes(fieldId))?.type;
}

function movesFor(type: string) {
    const loaded = loadedIds(type);
    const bySource = new Map<string, string[]>();

    for (const id of drafts[type] ?? []) {
        if (loaded.includes(id)) continue;
        const source = sourceTypeOf(id, type);
        if (!source) continue;
        const title = store.fields.find(x => x.id === id)?.title;
        bySource.set(source, [...(bySource.get(source) ?? []), title ? `${title} (${id})` : id]);
    }

    return [...bySource].map(([source, fields]) => ({ source, fields }));
}

function isDirty(type: string) {
    const draft = new Set(drafts[type] ?? []);
    const loaded = new Set(loadedIds(type));
    return draft.size !== loaded.size || [...draft].some(id => !loaded.has(id));
}

function resetDrafts(keep: Record<string, string[]> = {}) {
    for (const { type, fieldIds } of store.types) {
        drafts[type] = keep[type] ?? [...fieldIds];
    }
}

async function save(type: string) {
    if (busy.value) return;

    const fieldIds = [...(drafts[type] ?? [])];
    const previous = loadedIds(type);
    const movedIn = new Set(fieldIds.filter(id => !previous.includes(id)));
    saving[type] = true;

    try {
        await store.saveFieldType({ type, fieldIds });
        eventBus.emitter.emit(Events.alert, { type: AlertTypes.success, text: `Lagret: ${typeLabel(type)}` });

        const keep: Record<string, string[]> = {};
        for (const other of store.types) {
            if (other.type !== type && isDirty(other.type)) {
                keep[other.type] = drafts[other.type].filter(id => !movedIn.has(id));
            }
        }

        await store.loadFieldTypes();
        resetDrafts(keep);
    } catch (err: any) {
        const text = errorText(err);
        if (text === null) throw err;
        eventBus.emitter.emit(Events.alert, { type: AlertTypes.error, text });
    } finally {
        saving[type] = false;
    }
}
</script>

<style lang="scss" scoped>
.field-types-loader {
    width: 100%;
    background: transparent;
    max-width: 700px;
}
</style>
